import _ from 'lodash';
import guid from 'uuid';
import { DocumentsSource, Layer } from 'const';
import { CarouselItem } from 'containers/Layouts/sagas/addCarousel';
import { createLayeredGroupLayout, createLayeredLayout } from 'factories/layoutFactory';
import * as Models from 'models';
import { findDuplicateIdByDocumentId } from 'utils/findDocumentDuplicateId';
import { createLayers } from 'utils/layers';
import { addReusableLayout } from 'utils/reusableLayouts/addReusableLayout';

interface AddGroupReusableLayoutToArtboardResult {
  layouts: Models.LayeredCombinedLayouts;
  artboard: Models.Artboard;
  newImages: Models.Images;
  relations: Models.LayeredRelations;
  documents: Models.CombinedDocuments;
}

interface AddGroupReusableLayoutToArtboardArg {
  groupLayoutDocument: Models.GroupLayoutDocument;
  sectionId: string;
  position: number;
  artboard: Models.Artboard;
  projectDocuments: Models.CombinedDocuments;
  prevProjectDocuments?: Models.CombinedDocuments;
  layouts: Models.LayeredCombinedLayouts;
  projectBrandDefinition: Models.OnlineBrandDefinition;
  activeLayer: Layer;
  rootDocument: Models.RootDocument;
  storyCardId?: string;
  groupLayoutId?: string;
  adoptStyles?: boolean;
  carouselItem?: CarouselItem;
}

export function addGroupLayoutToArtboard({
  groupLayoutDocument,
  sectionId,
  position,
  artboard,
  projectDocuments,
  layouts,
  projectBrandDefinition,
  activeLayer,
  rootDocument,
  storyCardId,
  groupLayoutId,
  adoptStyles,
  carouselItem,
}: AddGroupReusableLayoutToArtboardArg): AddGroupReusableLayoutToArtboardResult {
  const result = {
    layouts,
    artboard,
    relations: {},
    newImages: {},
    documents: projectDocuments,
  } as AddGroupReusableLayoutToArtboardResult;
  const newGroupLayoutId = groupLayoutId || guid();
  result.artboard.layoutIds.splice(position, 0, newGroupLayoutId);

  const {
    id: groupLayoutDocumentInternalId,
    entities: { layouts: layoutIds, styles },
  } = groupLayoutDocument;

  const existingLayout = _.find(layouts, ({ documentId }) => documentId[activeLayer] === groupLayoutDocumentInternalId) as Models.LayeredGroupLayout;

  if (existingLayout) {
    const newLayoutIds = existingLayout.layoutIds.map((oldLayoutId) => {
      const newLayoutId = guid();
      result.layouts[newLayoutId] = createLayeredLayout({
        ...layouts[oldLayoutId] as Models.LayeredLayout,
        section: sectionId,
        id: newLayoutId,
      });

      return newLayoutId;
    });

    result.layouts[newGroupLayoutId] = createLayeredGroupLayout({
      ...existingLayout,
      id: newGroupLayoutId,
      layoutIds: newLayoutIds,
      section: sectionId,
    }, carouselItem);

    return result;
  }

  const newLayoutIds = layoutIds.map((childLayoutDocumentPMId) => {
    let childLayoutDocument = _.find<Models.ReusableLayout>(
      storyCardId ? _.get(result.documents, [storyCardId, 'documents']) : result.documents,
      document => document.documentId === childLayoutDocumentPMId,
    );

    const duplicateId = findDuplicateIdByDocumentId(
      childLayoutDocument,
      result.documents as Models.Documents,
    );
    if (duplicateId) {
      childLayoutDocument = result.documents[duplicateId] as Models.ReusableLayout;
    }

    const addLayoutResult = addReusableLayout(
      childLayoutDocument,
      sectionId,
      result.documents,
      layouts,
      projectBrandDefinition,
      activeLayer,
      rootDocument,
      undefined,
      adoptStyles ||
      // adapt styles force if RL was updated in Promomats
      _.get(result.documents, [childLayoutDocument.id, 'isUpdatedInPromoMats']),
    );

    _.assign(result.relations, addLayoutResult.relations);
    _.assign(result.documents, addLayoutResult.documents);
    _.assign(result.newImages, addLayoutResult.newImages);
    _.assign(result.layouts, { [addLayoutResult.layout.id]: addLayoutResult.layout });

    return addLayoutResult.layout.id;
  });

  result.layouts[newGroupLayoutId] = createLayeredGroupLayout({
    id: newGroupLayoutId,
    documentId: createLayers({ [activeLayer]: groupLayoutDocumentInternalId }),
    layoutIds: newLayoutIds,
    section: sectionId,
    styles,
  }, carouselItem);
  groupLayoutDocument.documentSource = _.union(groupLayoutDocument.documentSource, [DocumentsSource.COMPONENTS]);
  result.documents[groupLayoutDocumentInternalId] = groupLayoutDocument;

  return result;
}
